import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Loader2, Zap, Trophy, TrendingDown, TrendingUp, Users, Percent, Check, X, Download, Mail } from "lucide-react";
import html2pdf from "html2pdf.js";

export default function MaestroRecap() {
  const { code } = useParams();
  const nav = useNavigate();
  const hostToken = typeof window !== "undefined" ? localStorage.getItem(`maestro_host_${code}`) : null;
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    if (!hostToken) { nav("/play/maestro"); return; }
    (async () => {
      try {
        const r = await api.get(`/maestro/rooms/${code}/analytics`, { params: { host_token: hostToken } });
        setData(r.data);
      } catch (e) {
        const msg = e.response?.data?.detail || "Analytics indisponibles";
        setErr(msg);
        toast.error(msg);
      }
    })();
  }, [code, hostToken, nav]);

  if (err) {
    return (
      <div className="max-w-3xl mx-auto p-10 text-center">
        <p className="text-[#4A5568]">{err}</p>
        <button onClick={() => nav("/play/maestro")} className="mt-4 px-5 py-2 rounded-lg bg-[#1F3A5F] text-white text-sm font-bold">Retour</button>
      </div>
    );
  }
  if (!data) return <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-[#8B95A1]" /></div>;

  const { hardest, easiest, fastest_player, per_question, per_player, participation_pct, n_players, n_questions, title } = data;

  const downloadPdf = () => {
    if (!printRef.current) return;
    setPdfBusy(true);
    const opt = {
      margin: 8,
      filename: `Maestro-Recap-${(title || "").replace(/\s+/g, "-")}-${code}.pdf`,
      image: { type: "jpeg", quality: 0.95 },
      html2canvas: { scale: 2, useCORS: true, backgroundColor: "#FFFFFF" },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    };
    html2pdf().set(opt).from(printRef.current).save()
      .then(() => toast.success("PDF téléchargé"))
      .catch(() => toast.error("Impossible de générer le PDF"))
      .finally(() => setPdfBusy(false));
  };

  const emailRecap = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTo)) return toast.error("Email invalide");
    setEmailBusy(true);
    try {
      await api.post(`/maestro/rooms/${code}/analytics/email`, { host_token: hostToken, recipient: emailTo });
      toast.success(`Récap envoyé à ${emailTo}`);
      setEmailOpen(false);
      setEmailTo("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi impossible");
    } finally { setEmailBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-10 py-10" data-testid="maestro-recap">
      <Toaster theme="light" />
      <button onClick={() => nav("/play/maestro")} className="inline-flex items-center gap-1 text-sm text-[#8B95A1] hover:text-[#E5276A] mb-4">
        <ArrowLeft className="w-4 h-4" /> Retour aux quiz
      </button>
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <span className="chip-pink">Récap partie</span>
          <h1 className="font-display text-3xl md:text-4xl text-[#1F3A5F] mt-2">{title}</h1>
          <p className="text-sm text-[#4A5568] mt-1">Code {code} · {n_players} joueur{n_players > 1 ? "s" : ""} · {n_questions} question{n_questions > 1 ? "s" : ""}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={downloadPdf} disabled={pdfBusy} data-testid="recap-pdf-btn"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-[#1F3A5F]/15 text-sm font-bold text-[#1F3A5F] hover:border-[#E5276A] disabled:opacity-50">
            {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Télécharger PDF
          </button>
          <button onClick={() => setEmailOpen(true)} data-testid="recap-email-btn"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661]">
            <Mail className="w-4 h-4" /> Envoyer par email
          </button>
        </div>
      </div>

      <div ref={printRef}>

      {/* Highlights */}
      <div className="mt-6 grid md:grid-cols-3 gap-3" data-testid="maestro-recap-highlights">
        <HighlightCard icon={<TrendingDown className="w-5 h-5 text-[#8B0000]" />} label="Question la plus difficile"
          title={hardest?.question || "—"} sub={hardest ? `${hardest.accuracy}% de bonnes réponses` : ""}
          testid="recap-hardest" />
        <HighlightCard icon={<TrendingUp className="w-5 h-5 text-[#4CAF50]" />} label="Question la plus facile"
          title={easiest?.question || "—"} sub={easiest ? `${easiest.accuracy}% de bonnes réponses` : ""}
          testid="recap-easiest" />
        <HighlightCard icon={<Zap className="w-5 h-5 text-[#F9C548]" />} label="Joueur le plus rapide"
          title={fastest_player?.name || "—"}
          sub={fastest_player?.avg_correct_response_s != null ? `${fastest_player.avg_correct_response_s}s en moyenne sur ses bonnes réponses` : ""}
          testid="recap-fastest" />
      </div>

      {/* Participation */}
      <div className="mt-4 rounded-2xl bg-white border border-[#1F3A5F]/10 p-5 flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-[#FBF7F3] flex items-center justify-center">
          <Percent className="w-5 h-5 text-[#E5276A]" />
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#8B95A1]">Taux de participation</div>
          <div className="font-display text-2xl text-[#1F3A5F]">{participation_pct}%</div>
          <div className="mt-1 h-2 rounded-full bg-[#1F3A5F]/10 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#E5276A] to-[#F9C548]" style={{ width: `${participation_pct}%` }} />
          </div>
        </div>
      </div>

      {/* Per-question breakdown */}
      <h2 className="font-display text-2xl text-[#1F3A5F] mt-8 mb-3">Précision par question</h2>
      <div className="space-y-3" data-testid="maestro-recap-questions">
        {per_question.map((q) => (
          <div key={q.idx} className="rounded-2xl bg-white border border-[#1F3A5F]/10 p-5" data-testid={`recap-q-${q.idx}`}>
            <div className="flex items-baseline justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#8B95A1]">Q{q.idx + 1}</div>
                <div className="font-display text-lg text-[#1F3A5F] mt-0.5">{q.question}</div>
              </div>
              <div className="text-right">
                <div className="font-display text-2xl text-[#E5276A]">{q.accuracy}%</div>
                <div className="text-[10px] text-[#8B95A1]">{q.n_correct}/{q.n_answers} correct{q.n_correct > 1 ? "es" : "e"}</div>
              </div>
            </div>
            <div className="mt-3 grid sm:grid-cols-2 gap-2">
              {(q.choices || []).map((c, ci) => {
                const isCorrect = ci === q.correct_idx;
                const total = (q.tallies || []).reduce((a, b) => a + b, 0) || 1;
                const pct = Math.round(((q.tallies?.[ci] || 0) / total) * 100);
                return (
                  <div key={ci} className={`rounded-lg p-2.5 border ${isCorrect ? "border-[#4CAF50] bg-green-50" : "border-[#1F3A5F]/10 bg-[#FBF7F3]"}`}>
                    <div className="flex items-center gap-2 text-sm text-[#1F3A5F]">
                      {isCorrect ? <Check className="w-3.5 h-3.5 text-[#4CAF50] shrink-0" /> : <X className="w-3.5 h-3.5 text-[#8B95A1] shrink-0" />}
                      <span className="truncate flex-1">{c}</span>
                      <span className="text-xs text-[#4A5568] tabular-nums">{q.tallies?.[ci] || 0}</span>
                    </div>
                    <div className="mt-1.5 h-1.5 rounded-full bg-white overflow-hidden">
                      <div className="h-full" style={{ width: `${pct}%`, background: isCorrect ? "#4CAF50" : "#E5276A" }} />
                    </div>
                  </div>
                );
              })}
            </div>
            {q.avg_response_s != null && (
              <div className="mt-3 text-[11px] text-[#8B95A1]">Temps moyen de réponse : {q.avg_response_s}s</div>
            )}
          </div>
        ))}
      </div>

      {/* Player leaderboard with correctness */}
      <h2 className="font-display text-2xl text-[#1F3A5F] mt-8 mb-3">Joueurs</h2>
      <div className="rounded-2xl bg-white border border-[#1F3A5F]/10 divide-y divide-[#1F3A5F]/10" data-testid="maestro-recap-players">
        {per_player.map((p, i) => (
          <div key={p.id} className="flex items-center justify-between p-3 text-sm">
            <div className="flex items-center gap-3 min-w-0">
              <span className="w-7 text-[#8B95A1] tabular-nums text-xs">#{i + 1}</span>
              <span className="font-bold text-[#1F3A5F] truncate">{p.name}</span>
              {p.team && <span className="text-[10px] text-[#8B95A1] italic">{p.team}</span>}
            </div>
            <div className="flex items-center gap-4 text-xs text-[#4A5568]">
              <span>{p.n_correct}/{n_questions} <Check className="w-3 h-3 inline text-[#4CAF50]" /></span>
              {p.avg_correct_response_s != null && <span>{p.avg_correct_response_s}s</span>}
              <span className="font-display text-[#E5276A] text-base tabular-nums">{p.score}</span>
            </div>
          </div>
        ))}
      </div>
      </div>{/* /printRef */}

      <button onClick={() => nav("/play/maestro")} className="mt-8 w-full py-3 rounded-xl bg-[#1F3A5F] text-white font-bold hover:bg-[#2A4870]">
        Retour aux quiz
      </button>

      {emailOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end md:items-center justify-center md:p-4" data-testid="recap-email-modal"
          onClick={() => !emailBusy && setEmailOpen(false)}>
          <div onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-white shadow-2xl relative flex flex-col rounded-t-3xl md:rounded-3xl max-h-[92dvh]">
            <button onClick={() => setEmailOpen(false)} disabled={emailBusy} aria-label="Fermer"
              className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-[#FBF7F3] hover:bg-[#FCE4ED] text-[#1F3A5F] hover:text-[#E5276A] flex items-center justify-center shadow-sm">
              <X className="w-5 h-5" />
            </button>
            <div className="overflow-y-auto p-6">
            <div className="inline-flex items-center gap-2 chip-pink"><Mail className="w-3 h-3" /> Envoyer par email</div>
            <h3 className="font-display text-2xl text-[#1F3A5F] mt-2">Partager le récap</h3>
            <p className="text-sm text-[#4A5568] mt-1">Le récap complet (points forts, précision par question, classement) sera envoyé au destinataire.</p>
            <label className="text-[10px] font-bold uppercase tracking-widest text-[#8B95A1] mt-4 block">Email du destinataire</label>
            <input type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} data-testid="recap-email-input"
              placeholder="ex : nom@domaine.fr"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-[#1F3A5F]/15 focus:border-[#E5276A] outline-none" />
            <div className="mt-5 flex gap-2 justify-end">
              <button onClick={() => setEmailOpen(false)} disabled={emailBusy}
                className="px-4 py-2 rounded-xl border border-[#1F3A5F]/15 text-sm font-bold text-[#1F3A5F] hover:bg-[#FBF7F3]">
                Annuler
              </button>
              <button onClick={emailRecap} disabled={emailBusy || !emailTo} data-testid="recap-email-send"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661] disabled:opacity-50">
                {emailBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                Envoyer
              </button>
            </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function HighlightCard({ icon, label, title, sub, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-[#1F3A5F]/10 p-4" data-testid={testid}>
      <div className="flex items-center gap-2">
        {icon}
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#8B95A1]">{label}</div>
      </div>
      <div className="font-display text-base text-[#1F3A5F] mt-2 line-clamp-2">{title}</div>
      {sub && <div className="text-xs text-[#4A5568] mt-1">{sub}</div>}
    </div>
  );
}
